import {
  Box,
  Card,
  Chip,
  Stack,
  Typography,
  type ChipProps,
} from '@mui/material';

import { Button } from '@karehero/llama/components/atoms/Button/Button';
import { radius } from '@karehero/llama/theme/radius';

import {
  STATUS_COLOR,
  type ShortlistProvider,
} from '@/karehero-app/care/shortlists/fixtures';

export const statusChipColor = (
  status: ShortlistProvider['status'],
): ChipProps['color'] => STATUS_COLOR[status] as ChipProps['color'];

// Wrapper component: a provider on a published shortlist, as the care circle
// sees it — cover photo, name, status chip, address/distance, care type +
// rating chips, price, and the CAM's note. Unavailable providers stay listed
// (the family should see they were considered) but visibly muted.
const ProviderCard = ({
  provider,
  onOpen,
  onToggleSecured,
}: {
  provider: ShortlistProvider;
  onOpen: (provider: ShortlistProvider) => void;
  // The family marks a home secured (or clears it) from the card itself.
  // Omitted in read-only contexts.
  onToggleSecured?: (provider: ShortlistProvider) => void;
}) => {
  const unavailable = provider.status === 'Unavailable';
  const secured = provider.status === 'Secured';

  return (
    <Card variant='listItem' sx={{ p: 2 }} onClick={() => onOpen(provider)}>
      <Stack spacing={1.5} sx={unavailable ? { opacity: 0.6 } : undefined}>
        {/* cover photo */}
        <Box
          sx={{
            height: 120,
            borderRadius: `${radius.md}px`,
            bgcolor: 'custom.neutral.200',
          }}
        />

        <Stack
          sx={{ alignItems: 'center', justifyContent: 'space-between' }}
          direction='row'
          spacing={1}
        >
          <Typography sx={{ minWidth: 0 }} variant='h6'>
            {provider.name}
          </Typography>
          <Chip
            size='small'
            label={provider.status}
            color={statusChipColor(provider.status)}
          />
        </Stack>

        <Typography variant='body1' color='text.secondary'>
          {provider.address} · {provider.distance}
        </Typography>

        <Stack
          sx={{ flexWrap: 'wrap' }}
          direction='row'
          spacing={1.5}
          useFlexGap
        >
          {provider.careTypes.map((c) => (
            <Chip key={c} label={c} size='small' />
          ))}
          <Chip label={`Rating ${provider.rating}`} size='small' />
        </Stack>

        {/* price is the headline number — give it real weight */}
        <Stack sx={{ alignItems: 'baseline' }} direction='row' spacing={0.5}>
          <Typography variant='body2' color='text.secondary'>
            from
          </Typography>
          <Typography variant='h6'>{provider.price.split('/')[0]}</Typography>
          <Typography variant='body2' color='text.secondary'>
            /{provider.price.split('/')[1] ?? 'week'}
          </Typography>
        </Stack>
      </Stack>

      {/* CAM note — visible to the care circle, full strength even when the
          provider is unavailable (it explains why). */}
      {provider.note && (
        <Box
          sx={{
            bgcolor: 'custom.neutral.100',
            borderRadius: '8px',
            p: 1.5,
            mt: 1.5,
          }}
        >
          <Stack spacing={0.5}>
            <Typography variant='subtitle2' color='text.secondary'>
              Note from your care expert
            </Typography>
            <Typography variant='body1'>{provider.note}</Typography>
          </Stack>
        </Box>
      )}

      {/* The family secures a home (or clears it) here. Stop the click from
          bubbling to the card, which opens the detail view. Hidden for
          unavailable homes — there's nothing to secure. */}
      {onToggleSecured && !unavailable && (
        <Box sx={{ mt: 1.5 }} onClick={(e) => e.stopPropagation()}>
          <Button
            variant='secondary'
            size='md'
            isFullWidth
            ariaLabel={secured ? 'Mark as not secured' : 'Mark as secured'}
            onPress={() => onToggleSecured(provider)}
          >
            {secured ? 'Mark as not secured' : 'Mark as secured'}
          </Button>
        </Box>
      )}
    </Card>
  );
};

export default ProviderCard;
